import {
  Text,
  Select,
  Stack,
  Alert,
  Button,
  Group,
  TextInput,
  Box,
  Checkbox,
  Paper,
  Badge,
  CloseButton,
} from '@mantine/core';
import { Info, User } from 'react-feather';
import type { PartnerProgramStepProps } from './types';
import { CREATE_NEW_PROGRAM_VALUE } from './wizardValidation';
import classes from './CreateCohortPage.module.css';

export function PartnerProgramStep({
  form,
  partners,
  programs,
  partnerContacts,
  loadingPartners,
  loadingPrograms,
  loadingContacts,
  onNavigateToPartners,
}: PartnerProgramStepProps) {
  const partnerOptions = partners.map(partner => ({
    value: partner.id,
    label: partner.code ? `${partner.code} - ${partner.name}` : partner.name,
  }));

  const programOptions = [
    ...programs.map(program => ({
      value: program.id,
      label: program.name,
    })),
    { value: CREATE_NEW_PROGRAM_VALUE, label: '+ Create new program' },
  ];

  const isCreatingNewProgram = form.values.programId === CREATE_NEW_PROGRAM_VALUE;

  // Get selected contacts for display
  const selectedContacts = partnerContacts.filter(c =>
    form.values.primaryContactIds.includes(c.id)
  );

  const handleContactToggle = (contactId: string) => {
    const current = form.values.primaryContactIds;
    if (current.includes(contactId)) {
      form.setFieldValue('primaryContactIds', current.filter(id => id !== contactId));
    } else {
      form.setFieldValue('primaryContactIds', [...current, contactId]);
    }
  };

  const handleRemoveContact = (contactId: string) => {
    form.setFieldValue(
      'primaryContactIds',
      form.values.primaryContactIds.filter(id => id !== contactId)
    );
  };

  return (
    <Stack gap="lg">
      {/* Section Header */}
      <Group gap="xs">
        <User size={20} />
        <Text size="lg" fw={600}>Partner & Program</Text>
      </Group>

      {!loadingPartners && partners.length === 0 ? (
        <Alert icon={<Info size={16} />} color="orange" title="No partners available">
          You need to create a partner organization before creating a cohort.
          <Button variant="light" color="orange" size="xs" mt="sm" onClick={onNavigateToPartners}>
            Go to Partners
          </Button>
        </Alert>
      ) : (
        <>
          {/* Partner & Program on same row */}
          <Group grow align="flex-start">
            <Select
              label="Partner / Client"
              placeholder="Select a partner..."
              data={partnerOptions}
              // Mantine clears the shown text only for null, not for ''.
              value={form.values.partnerId || null}
              onChange={(value) => form.setFieldValue('partnerId', value || '')}
              error={form.errors.partnerId}
              searchable
              required
              disabled={loadingPartners}
            />

            <Select
              label="Program"
              placeholder={form.values.partnerId ? 'Select program...' : 'Select partner first'}
              data={programOptions}
              // The partner change clears programId to ''. Pass null so Mantine
              // also clears the old program name from the field.
              value={form.values.programId || null}
              onChange={(value) => {
                form.setFieldValue('programId', value || '');
                // Clear new program name if not creating new
                if (value !== CREATE_NEW_PROGRAM_VALUE) {
                  form.setFieldValue('newProgramName', '');
                }
              }}
              error={form.errors.programId}
              disabled={!form.values.partnerId || loadingPrograms}
              required
            />
          </Group>

          {/* New Program Name - shown when creating new program */}
          {isCreatingNewProgram && (
            <TextInput
              label="New Program Name"
              placeholder="Enter program name..."
              value={form.values.newProgramName}
              onChange={(e) => form.setFieldValue('newProgramName', e.currentTarget.value)}
              error={form.errors.newProgramName}
              required
            />
          )}

          {/* Primary Partner Contacts */}
          {form.values.partnerId && (
            <Box>
              <Text size="sm" fw={500} mb="xs">
                Primary Partner Contacts <Text span c="dimmed" fw={400}>(optional)</Text>
              </Text>

              {/* Selected contacts as chips */}
              {selectedContacts.length > 0 && (
                <Paper withBorder p="xs" mb="sm" className={classes.contactChipsContainer}>
                  <Group gap="xs">
                    {selectedContacts.map(contact => (
                      <Badge
                        key={contact.id}
                        variant="filled"
                        color="medleyGreen"
                        rightSection={
                          <CloseButton
                            size="xs"
                            onClick={() => handleRemoveContact(contact.id)}
                            aria-label={`Remove ${contact.first_name}`}
                          />
                        }
                      >
                        {contact.first_name} {contact.last_name}
                      </Badge>
                    ))}
                  </Group>
                </Paper>
              )}

              {/* Contact list with checkboxes */}
              {loadingContacts ? (
                <Text size="sm" c="dimmed">Loading contacts...</Text>
              ) : partnerContacts.length === 0 ? (
                <Text size="sm" c="dimmed">No contacts available for this partner</Text>
              ) : (
                <Paper withBorder p="xs">
                  <Stack gap="xs">
                    {partnerContacts.map(contact => (
                      <Group
                        key={contact.id}
                        gap="sm"
                        className={classes.contactRow}
                        onClick={() => handleContactToggle(contact.id)}
                        style={{ cursor: 'pointer' }}
                      >
                        <Checkbox
                          checked={form.values.primaryContactIds.includes(contact.id)}
                          onChange={() => handleContactToggle(contact.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <Box>
                          <Text size="sm" fw={500}>{contact.first_name} {contact.last_name}</Text>
                          <Text size="xs" c="dimmed">{contact.email}</Text>
                        </Box>
                      </Group>
                    ))}
                  </Stack>
                </Paper>
              )}
            </Box>
          )}
        </>
      )}
    </Stack>
  );
}
